// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/otp-input.js
import BaseComponent from "./base_component"

export default class OTPInput extends BaseComponent {
  static componentName = "otp-input"
  static defaults = { type: "number", linear: true, masked: false, disabled: false, readonly: false }

  constructor(element, config = {}) {
    super(element, config)
    this.inputs = [...element.querySelectorAll(".form-otp-control")]
    this.hidden = document.createElement("input")
    this.hidden.type = "hidden"
    if (this.config.name) this.hidden.name = this.config.name
    this.hidden.disabled = Boolean(this.config.disabled)
    element.append(this.hidden)
    element.setAttribute("role", "group")
    const initialValue = this.config.value ?? this.inputs.map((input) => input.value).join("")
    this.inputs.forEach((input, index) => {
      input.removeAttribute("name")
      input.removeAttribute("maxlength")
      if (this.config.required !== undefined) input.required = Boolean(this.config.required)
      input.type = this.config.masked ? "password" : "text"
      // Do not set maxlength: autofill may put the whole code in one field.
      input.inputMode = this.config.type === "number" ? "numeric" : "text"
      input.autocomplete = index === 0 ? "one-time-code" : "off"
      input.disabled = Boolean(this.config.disabled)
      input.readOnly = Boolean(this.config.readonly)
      input.setAttribute("aria-label", `Digit ${index + 1} of ${this.inputs.length}`)
      this.listen(input, "beforeinput", (event) => {
        if (event.inputType === "insertText" && event.data?.length === 1 && !this.normalize(event.data))
          event.preventDefault()
      })
      this.listen(input, "input", () => this.enter(index, input.value))
      this.listen(input, "paste", (event) => {
        event.preventDefault()
        this.enter(index, event.clipboardData.getData("text"))
      })
      this.listen(input, "focus", () => input.select())
      this.listen(input, "keydown", (event) => {
        if (input.disabled || input.readOnly) return
        if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
          event.preventDefault()
          const next =
            event.key === "Home"
              ? 0
              : event.key === "End"
                ? this.inputs.length - 1
                : index + (event.key === "ArrowRight" ? 1 : -1)
          this.inputs[next]?.focus()
        }
        if (event.key === "Backspace") {
          event.preventDefault()
          const target = input.value ? index : Math.max(0, index - 1)
          this.inputs[target].value = ""
          this.inputs[target].focus()
          this.changed()
        }
      })
    })
    this.setValue(String(initialValue))
  }

  normalize(value) {
    return this.config.type === "number" ? value.replace(/\D/g, "") : value.replace(/\s/g, "")
  }

  enter(index, value) {
    if (this.config.disabled || this.config.readonly) return
    const characters = [...this.normalize(value)]
    if (value && !characters.length) {
      this.inputs[index].value = this.slotValues?.[index] || ""
      return
    }
    this.inputs[index].value = ""
    characters.slice(0, this.inputs.length - index).forEach((character, offset) => {
      this.inputs[index + offset].value = character
    })
    this.changed()
    this.inputs[Math.min(index + characters.length, this.inputs.length - 1)].focus()
  }

  setValue(value) {
    const characters = [...this.normalize(value)]
    this.inputs.forEach((input, index) => {
      input.value = characters[index] || ""
    })
    this.changed()
  }

  changed() {
    this.slotValues = this.inputs.map((input) => input.value)
    this.hidden.value = this.slotValues.join("")
    const firstEmpty = this.inputs.findIndex((input) => !input.value)
    this.inputs.forEach((input, index) => {
      input.tabIndex = !this.config.linear || index === (firstEmpty < 0 ? 0 : firstEmpty) ? 0 : -1
    })
    this.emit("change", { value: this.hidden.value })
    if (this.inputs.every((input) => input.value)) this.emit("complete", { value: this.hidden.value })
  }

  clear() {
    this.setValue("")
  }

  dispose() {
    if (this.signal.aborted) return
    this.element.innerHTML = this.originalHTML
    this.restoreAttribute("role")
    super.dispose()
  }
  snapshotState() {
    return { slots: this.inputs.map((input) => input.value) }
  }
  restoreState(state) {
    if (!state.slots) return
    this.inputs.forEach((input, index) => {
      input.value = state.slots[index] || ""
    })
    this.changed()
  }
}
